import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "October 23, 2026";

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg md:text-lg font-black font-display text-slate-900">{title}</h2>
      <div className="mt-2 space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/login"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <img src="/cfaa-mark.png" alt="CFAA" className="h-14 w-auto rounded-lg" />
          <div>
            <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight text-slate-900">Terms of Service</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <p className="mt-6 text-sm text-slate-600 leading-relaxed">
          These Terms of Service ("Terms") govern your access to and use of the Crusader Football Alumni Association
          ("CFAA", "we", "us", or "our") application and website. By creating an account or using the service, you agree
          to these Terms. If you do not agree, do not use the service.
        </p>

        <Section title="1. Who can use CFAA">
          <p>
            Membership is intended for former University of Mary Hardin–Baylor Crusader football players, coaches,
            staff, and approved friends of the program. You must be at least 13 years old and provide accurate
            information. CFAA may approve, decline, or revoke access at its sole discretion.
          </p>
        </Section>

        <Section title="2. Your account">
          <p>
            You are responsible for keeping your login credentials confidential and for all activity on your account.
            Notify us immediately at <a className="text-purple-700 font-semibold" href="mailto:contact@cfaa.app">contact@cfaa.app</a> if
            you believe your account has been compromised. CFAA is not liable for losses caused by unauthorized use of
            your account.
          </p>
        </Section>

        <Section title="3. Membership tiers & dues">
          <p>
            CFAA offers paid membership tiers (Monthly, Annual, Excalibur Monthly, Excalibur Annual, and Lifetime), as
            well as optional one-time donations. Prices are shown in U.S. dollars and are processed in test or live mode
            via Stripe, Inc. Current pricing is displayed in the app and may change on notice.
          </p>
          <p>
            <span className="font-bold">Recurring subscriptions.</span> If you choose a monthly or annual recurring tier,
            your card on file will be charged automatically at the start of each billing cycle until you cancel. The
            renewal amount equals the tier's listed price at the time of renewal plus any applicable taxes. You can
            cancel at any time from the Membership page; cancellation takes effect at the end of the current billing
            period, and you retain access until then.
          </p>
          <p>
            <span className="font-bold">Refunds.</span> Dues and donations are generally non-refundable. If you believe
            a charge is in error, contact us within 30 days and we will review in good faith.
          </p>
          <p>
            <span className="font-bold">Lifetime membership.</span> A single one-time charge grants ongoing access for
            the life of the service. If CFAA permanently discontinues the service, lifetime membership obligations end
            on that date with no refund owed.
          </p>
        </Section>

        <Section title="4. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Harass, bully, threaten, or discriminate against any member;</li>
            <li>Post unlawful, sexually explicit, violent, or infringing content;</li>
            <li>Impersonate another person or misrepresent your affiliation;</li>
            <li>Scrape, reverse-engineer, or interfere with the service;</li>
            <li>Share login credentials or allow another person to use your account;</li>
            <li>Use the service for unauthorized commercial solicitation or spam.</li>
          </ul>
          <p>
            CFAA may remove any content and suspend or terminate any account that violates these Terms, with or without
            notice.
          </p>
        </Section>

        <Section title="5. Your content">
          <p>
            You retain ownership of the messages, photos, polls, and comments you post ("Your Content"). By posting, you
            grant CFAA a worldwide, non-exclusive, royalty-free license to host, display, and distribute Your Content
            within the service for the purpose of operating and improving CFAA. You represent that you have the right to
            post Your Content and that it does not violate anyone's rights.
          </p>
        </Section>

        <Section title="6. Content from CFAA">
          <p>
            The CFAA logo, name, trademarks, photographs, event materials, and software are owned by CFAA or its
            licensors and are protected by copyright and trademark laws. You may not use them outside the service
            without prior written permission.
          </p>
        </Section>

        <Section title="7. Third-party services">
          <p>
            CFAA uses third-party services to deliver the app, including Stripe (payments), Emergent (hosting and
            authentication), and Google (optional sign-in). Their terms govern their portion of the service, and we are
            not responsible for their acts or omissions.
          </p>
        </Section>

        <Section title="8. Disclaimers">
          <p>
            The service is provided <span className="font-bold">"as is" and "as available"</span> without warranties of
            any kind. CFAA does not warrant that the service will be uninterrupted, error-free, or secure, or that any
            particular information will be accurate.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the fullest extent permitted by law, CFAA, its officers, directors, volunteers, and contractors will not
            be liable for any indirect, incidental, special, consequential, or punitive damages, or loss of profits or
            data, arising out of or related to your use of the service. CFAA's total liability for any claim will not
            exceed the amount you paid CFAA in the twelve (12) months before the event giving rise to the claim.
          </p>
        </Section>

        <Section title="10. Indemnification">
          <p>
            You agree to indemnify and hold harmless CFAA and its officers, directors, volunteers, and contractors from
            any claims, damages, or expenses (including reasonable attorneys' fees) arising from your violation of these
            Terms or your misuse of the service.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            You may close your account at any time by contacting{" "}
            <a className="text-purple-700 font-semibold" href="mailto:contact@cfaa.app">contact@cfaa.app</a>. CFAA may
            suspend or terminate access at any time with or without cause. Sections intended to survive termination
            (payment obligations, content license, disclaimers, limitation of liability, indemnification, dispute
            resolution) will survive.
          </p>
        </Section>

        <Section title="12. Changes to these Terms">
          <p>
            We may update these Terms from time to time. If we make material changes, we will post the updated Terms
            here and update the "Last updated" date. Your continued use after the effective date constitutes acceptance.
          </p>
        </Section>

        <Section title="13. Governing law & disputes">
          <p>
            These Terms are governed by the laws of the State of Texas, without regard to its conflict-of-laws rules.
            You and CFAA agree to resolve any dispute in the state or federal courts located in Bell County, Texas, and
            you consent to personal jurisdiction there. Nothing in these Terms prevents either party from seeking
            emergency equitable relief.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            Questions about these Terms? Email{" "}
            <a className="text-purple-700 font-semibold" href="mailto:contact@cfaa.app">contact@cfaa.app</a>.
          </p>
        </Section>

        <div className="mt-10 border-t border-slate-200 pt-6 flex items-center justify-between">
          <Link to="/privacy" data-testid="terms-to-privacy" className="text-sm font-semibold text-purple-700 hover:text-purple-900">
            Privacy Policy →
          </Link>
          <Link to="/welcome" data-testid="terms-to-welcome" className="text-sm font-semibold text-purple-700 hover:text-purple-900">
            Back to Welcome
          </Link>
        </div>
      </div>
    </div>
  );
}
